import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Stack, Typography, Collapse, IconButton } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { ButtonDeleteIcon } from "@ippon/shared-assets";
import {
  CustomButton,
  CustomMiddleSelect,
  CustomTextInput,
  colors,
} from "@ippon/shared-components";
import useGlobalToast from "@/hooks/useGlobalToast";
import { useCan } from "@/hooks/useCan";
import { Can } from "@/components/auth/Can";
import type { AppDispatch, RootState } from "@/store";
import {
  fetchBudgetCategories,
  checkDeleteBudgetCategory,
  createBudgetCategory,
  updateBudgetCategory,
  deleteBudgetCategory,
  type BudgetCategoryRecord,
  type BudgetCategoryType,
  type BudgetRevenueGroup,
} from "@/store/budgetSlice";
import {
  SortableTextRow,
  restrictToVerticalAxis,
  restrictToParentElement,
  type PulldownEditorHandle,
} from "./pulldownSortable";

interface Bucket {
  id: string;
  label: string;
  type: BudgetCategoryType;
  /** 収入のみ。BE は任意文字列も許容するため string で保持。 */
  revenueGroup?: string;
  /** 支出のみ。カテゴリ列の表示・絞り込みに使う上位カテゴリ。 */
  expenseGroup?: string;
  /** 標準(固定)科目の表示を抑制する (収入/その他、支出は標準科目を持たない扱い)。 */
  hideStandard?: boolean;
}

const isExpenseType = (type: string) =>
  type === "expense" || type === "expense_opex" || type === "expense_capex";

// 常に表示する標準バケット (科目区分 × 収益カテゴリ)。ここに「追加」すると区分が確定する。
const KNOWN_BUCKETS: Bucket[] = [
  {
    id: "income|stock_rent",
    label: "貸室",
    type: "income",
    revenueGroup: "stock_rent",
  },
  {
    id: "income|stock_other",
    label: "付帯",
    type: "income",
    revenueGroup: "stock_other",
  },
  // 収入/その他は標準科目を表示しない (ユーザーが任意に追加する区分)
  {
    id: "income|other",
    label: "その他",
    type: "income",
    revenueGroup: "other",
    hideStandard: true,
  },
];

const catBucketId = (c: BudgetCategoryRecord) =>
  c.category_type === "income"
    ? `${c.category_type}|${c.revenue_group ?? ""}`
    : isExpenseType(c.category_type)
      ? `expense|${c.expense_group ?? ""}`
      : `${c.category_type}|`;

const expenseCategoryFieldId = (bucketId: string) =>
  `expense_category:${bucketId}`;

interface EditableRow {
  id: string;
  isNew: boolean;
}

type BudgetCategoryPane = "income" | "expense";

/**
 * 予実科目マスタの管理。収入は revenue_group、支出は expense_group ごとの
 * バケットで編集する。
 * カスタムフォーム踏襲: 標準科目は「固定項目」風の表示専用 (折りたたみ)、
 * カスタム科目は他のプルダウンと同じ DnD 行 + ダッシュの追加ボタンで編集する。
 * 科目区分・収益カテゴリはバケット (区分) から自動的に決まる。
 *
 * - 収入の標準科目 (is_system) は編集・削除不可。
 * - 支出は is_system を見ず、BudgetCategory の支出明細をカテゴリ別にそのまま編集対象にする。
 * - 保存ボタンは持たず、下部 FixedActionBar の「更新」から親が save() を呼ぶ
 *   (forwardRef + useImperativeHandle)。差分をまとめてコミット
 *   (追加=POST / リネーム=PATCH / 削除=DELETE)。削除は論理削除で、
 *   使用中の科目は BE が 409 を返すので失敗メッセージとして親へ返す。
 */
interface BudgetCategorySectionProps {
  /** コミット中は操作を抑制する */
  disabled?: boolean;
  /** dirty 状態が変わったら親へ通知 (下部更新ボタンの活性制御用) */
  onDirtyChange?: (dirty: boolean) => void;
}

const BudgetCategorySection = forwardRef<
  PulldownEditorHandle,
  BudgetCategorySectionProps
>(function BudgetCategorySection({ disabled, onDirtyChange }, ref) {
  const dispatch = useDispatch<AppDispatch>();
  const { showToast } = useGlobalToast();
  const can = useCan();
  const cats = useSelector(
    (s: RootState) => s.budgets.categories.data,
  ) as BudgetCategoryRecord[];

  const [bucketRows, setBucketRows] = useState<Record<string, EditableRow[]>>(
    {},
  );
  const [expenseCategoryRows, setExpenseCategoryRows] = useState<EditableRow[]>(
    [],
  );
  const [deletedExpenseBucketIds, setDeletedExpenseBucketIds] = useState<
    string[]
  >([]);
  const [expandedSystem, setExpandedSystem] = useState<Record<string, boolean>>(
    {},
  );
  const [activePane, setActivePane] = useState<BudgetCategoryPane>("income");
  const counterRef = useRef(0);
  const initialNamesRef = useRef<Record<string, string>>({});
  const initialExpenseCategoryNamesRef = useRef<Record<string, string>>({});

  const { register, getValues, setValue, reset, watch } = useForm<
    Record<string, string>
  >({ defaultValues: {} });
  const watched = watch();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const expenseBuckets = useMemo<Bucket[]>(
    () =>
      expenseCategoryRows.map((row) => {
        const fieldId = expenseCategoryFieldId(row.id);
        const name = (
          watched[fieldId] ??
          initialExpenseCategoryNamesRef.current[row.id] ??
          ""
        ).trim();
        return {
          id: row.id,
          label: `支出 / ${name || "新しいカテゴリ"}`,
          type: "expense_opex",
          expenseGroup: name,
          hideStandard: true,
        };
      }),
    [expenseCategoryRows, watched],
  );

  const buckets = useMemo(
    () => [...KNOWN_BUCKETS, ...expenseBuckets],
    [expenseBuckets],
  );

  const { systemByBucket, customByBucket } = useMemo(() => {
    const sys: Record<string, BudgetCategoryRecord[]> = {};
    const cus: Record<string, BudgetCategoryRecord[]> = {};
    for (const c of cats) {
      if ((c as BudgetCategoryRecord & { deleted_at?: string }).deleted_at)
        continue;
      // 支出のカテゴリ(親, level="category")はバケット見出しを作るだけで、
      // 明細行(customByBucket)には含めない (親をアイテムとして2重表示しない)。
      if (
        isExpenseType(c.category_type) &&
        (c as BudgetCategoryRecord & { level?: string }).level === "category"
      ) {
        continue;
      }
      const id = catBucketId(c);
      const target = !isExpenseType(c.category_type) && c.is_system ? sys : cus;
      (target[id] ??= []).push(c);
    }
    const byOrder = (a: BudgetCategoryRecord, b: BudgetCategoryRecord) =>
      (a.display_order ?? 0) - (b.display_order ?? 0);
    for (const k in sys) sys[k].sort(byOrder);
    for (const k in cus) cus[k].sort(byOrder);
    return { systemByBucket: sys, customByBucket: cus };
  }, [cats]);

  // サーバ値が変わったら編集状態をリセット
  const catsKey = useMemo(
    () =>
      cats
        .map(
          (c) =>
            `${c.category_id}:${c.name}:${c.display_order}:${catBucketId(c)}`,
        )
        .join("|"),
    [cats],
  );
  useEffect(() => {
    const rowsByBucket: Record<string, EditableRow[]> = {};
    const expenseRows: EditableRow[] = [];
    const names: Record<string, string> = {};
    const expenseNames: Record<string, string> = {};
    const values: Record<string, string> = {};
    for (const b of KNOWN_BUCKETS) {
      const custom = customByBucket[b.id] ?? [];
      rowsByBucket[b.id] = custom.map((c) => ({
        id: c.category_id,
        isNew: false,
      }));
      for (const c of custom) {
        names[c.category_id] = c.name;
        values[c.category_id] = c.name;
      }
    }
    const expenseGroupNames = Array.from(
      new Set(
        cats
          .filter(
            (c) =>
              isExpenseType(c.category_type) &&
              !(c as BudgetCategoryRecord & { deleted_at?: string })
                .deleted_at &&
              !!c.expense_group,
          )
          .map((c) => c.expense_group as string),
      ),
    );
    for (const groupName of expenseGroupNames) {
      const bucketId = `expense|${groupName}`;
      expenseRows.push({ id: bucketId, isNew: false });
      expenseNames[bucketId] = groupName;
      values[expenseCategoryFieldId(bucketId)] = groupName;
      const custom = customByBucket[bucketId] ?? [];
      rowsByBucket[bucketId] = custom.map((c) => ({
        id: c.category_id,
        isNew: false,
      }));
      for (const c of custom) {
        names[c.category_id] = c.name;
        values[c.category_id] = c.name;
      }
    }
    initialNamesRef.current = names;
    initialExpenseCategoryNamesRef.current = expenseNames;
    reset(values);
    setBucketRows(rowsByBucket);
    setExpenseCategoryRows(expenseRows);
    setDeletedExpenseBucketIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catsKey, reset]);

  const handleAdd = (bucketId: string) => {
    const id = `new_${counterRef.current++}`;
    setBucketRows((prev) => ({
      ...prev,
      [bucketId]: [...(prev[bucketId] ?? []), { id, isNew: true }],
    }));
    setValue(id, "");
  };
  const handleDelete = (bucketId: string, id: string) => {
    setBucketRows((prev) => ({
      ...prev,
      [bucketId]: (prev[bucketId] ?? []).filter((r) => r.id !== id),
    }));
  };
  const handleAddExpenseCategory = () => {
    const bucketId = `expense_new_${counterRef.current++}`;
    const detailId = `new_${counterRef.current++}`;
    setExpenseCategoryRows((prev) => [...prev, { id: bucketId, isNew: true }]);
    setBucketRows((prev) => ({
      ...prev,
      [bucketId]: [{ id: detailId, isNew: true }],
    }));
    setValue(expenseCategoryFieldId(bucketId), "");
    setValue(detailId, "");
  };
  const handleDeleteExpenseCategory = (bucketId: string) => {
    setExpenseCategoryRows((prev) => prev.filter((row) => row.id !== bucketId));
    setBucketRows((prev) => ({ ...prev, [bucketId]: [] }));
    if (!bucketId.startsWith("expense_new_")) {
      setDeletedExpenseBucketIds((prev) =>
        prev.includes(bucketId) ? prev : [...prev, bucketId],
      );
    }
  };
  const formatDeleteError = (
    name: string,
    payload?: { message?: string },
  ): string => {
    if (payload?.message?.includes("予実データで使用中")) {
      return `${name}は予実データで使用中のため削除できません。先に予実表から該当データを削除してください。`;
    }
    return `${name}: ${payload?.message ?? "削除に失敗しました"}`;
  };
  const collectPendingDeleteCategories = () => {
    const items: Array<{ id: string; name: string }> = [];
    for (const bucketId of deletedExpenseBucketIds) {
      for (const c of customByBucket[bucketId] ?? []) {
        items.push({ id: c.category_id, name: c.name });
      }
    }
    for (const b of buckets) {
      const rows = bucketRows[b.id] ?? [];
      const rowIds = new Set(rows.filter((r) => !r.isNew).map((r) => r.id));
      for (const c of customByBucket[b.id] ?? []) {
        if (!rowIds.has(c.category_id)) {
          items.push({ id: c.category_id, name: c.name });
        }
      }
    }
    const seen = new Set<string>();
    return items.filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  };
  const validatePendingDeleteCategories = async (): Promise<string[]> => {
    const errors: string[] = [];
    for (const item of collectPendingDeleteCategories()) {
      const result = await dispatch(checkDeleteBudgetCategory(item.id));
      if (checkDeleteBudgetCategory.fulfilled.match(result)) {
        if (!result.payload.can_delete) {
          errors.push(
            result.payload.reason ?? `${item.name}は削除できません。`,
          );
        }
      } else {
        errors.push(
          `${item.name}: ${result.payload?.message ?? "削除可否の確認に失敗しました"}`,
        );
      }
    }
    return errors;
  };
  const handleDragEnd = (bucketId: string, e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const rows = bucketRows[bucketId] ?? [];
    const oldIndex = rows.findIndex((r) => r.id === active.id);
    const newIndex = rows.findIndex((r) => r.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(rows, oldIndex, newIndex);
    setBucketRows((prev) => ({ ...prev, [bucketId]: reordered }));

    // 並び替えは即時保存 (更新ボタンとは独立)。既存科目の display_order 群を
    // 新しい並び順へ再割り当てして即 PATCH。未保存の追加行は保存時に末尾採番されるため対象外。
    const customCats = customByBucket[bucketId] ?? [];
    const orders = customCats
      .map((c) => c.display_order ?? 0)
      .sort((a, b) => a - b);
    reordered
      .filter((r) => !r.isNew)
      .forEach((r, i) => {
        const cat = customCats.find((c) => c.category_id === r.id);
        const target = orders[i];
        if (cat && target !== undefined && cat.display_order !== target) {
          void dispatch(
            updateBudgetCategory({ category_id: r.id, display_order: target }),
          ).then((res) => {
            if (!updateBudgetCategory.fulfilled.match(res)) {
              showToast({
                type: "error",
                message: "並び替えの保存に失敗しました",
              });
            }
          });
        }
      });
  };

  const dirty = useMemo(() => {
    if (deletedExpenseBucketIds.length > 0) return true;
    for (const row of expenseCategoryRows) {
      const fieldId = expenseCategoryFieldId(row.id);
      const cur = (watched[fieldId] ?? "").trim();
      const orig = initialExpenseCategoryNamesRef.current[row.id] ?? "";
      if (row.isNew && cur) return true;
      if (!row.isNew && cur !== orig) return true;
    }
    for (const b of buckets) {
      const rows = bucketRows[b.id] ?? [];
      const orig = customByBucket[b.id] ?? [];
      const rowIds = new Set(rows.filter((r) => !r.isNew).map((r) => r.id));
      if (orig.some((c) => !rowIds.has(c.category_id))) return true; // 削除
      if (rows.some((r) => r.isNew && (watched[r.id] ?? "").trim()))
        return true; // 追加
      for (const r of rows) {
        if (r.isNew) continue;
        const cur = (
          watched[r.id] ??
          initialNamesRef.current[r.id] ??
          ""
        ).trim();
        if (cur !== (initialNamesRef.current[r.id] ?? "")) return true; // 改名
      }
    }
    return false;
  }, [
    buckets,
    bucketRows,
    customByBucket,
    deletedExpenseBucketIds,
    expenseCategoryRows,
    watched,
  ]);

  // dirty の変化を親へ通知 (下部更新ボタンの活性制御)
  useEffect(() => {
    onDirtyChange?.(dirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty]);

  // 差分をコミットし、失敗メッセージ配列を返す (空=成功)。下部 FixedActionBar から呼ばれる。
  const commit = async (): Promise<string[]> => {
    if (!dirty) return [];
    const errors: string[] = [];
    try {
      const expenseCategoryNames = expenseCategoryRows
        .map((row) => ({
          row,
          name: (getValues(expenseCategoryFieldId(row.id)) ?? "").trim(),
        }))
        .filter(({ name }) => !!name);
      const duplicatedExpenseCategory = expenseCategoryNames.find(
        ({ name }, idx) =>
          expenseCategoryNames.findIndex((item) => item.name === name) !== idx,
      );
      if (duplicatedExpenseCategory) {
        return [
          `支出カテゴリ「${duplicatedExpenseCategory.name}」が重複しています`,
        ];
      }

      for (const bucketId of deletedExpenseBucketIds) {
        // 先に明細科目(子)を削除してから親カテゴリを削除する (親削除ガード対策)。
        for (const c of customByBucket[bucketId] ?? []) {
          const r = await dispatch(deleteBudgetCategory(c.category_id));
          if (!deleteBudgetCategory.fulfilled.match(r)) {
            errors.push(formatDeleteError(c.name, r.payload));
          }
        }
        // 親カテゴリ(level="category", 非system)も削除。system 標準7カテゴリは残す。
        const groupName = bucketId.startsWith("expense|")
          ? bucketId.slice("expense|".length)
          : "";
        const parent = cats.find(
          (c: any) =>
            !c.deleted_at &&
            c.level === "category" &&
            !c.is_system &&
            (c.expense_group ?? "") === groupName,
        );
        if (parent) {
          const pr = await dispatch(deleteBudgetCategory(parent.category_id));
          if (!deleteBudgetCategory.fulfilled.match(pr)) {
            errors.push(formatDeleteError(parent.name, pr.payload));
          }
        }
      }

      for (const b of buckets) {
        const rows = bucketRows[b.id] ?? [];
        const orig = customByBucket[b.id] ?? [];
        const rowIds = new Set(rows.filter((r) => !r.isNew).map((r) => r.id));
        const isExpenseBucket = b.id.startsWith("expense");
        const expenseGroup = isExpenseBucket
          ? (getValues(expenseCategoryFieldId(b.id)) ?? "").trim()
          : undefined;
        const hasInputDetail = rows.some((row) =>
          (getValues(row.id) ?? "").trim(),
        );

        if (isExpenseBucket && !expenseGroup && hasInputDetail) {
          errors.push("支出カテゴリ名を入力してください");
          continue;
        }
        // 親子モデル: カテゴリ(親)は明細科目0でも成立する(空カテゴリOK)。
        // 「明細科目を1件以上」の制約は撤廃。

        // 削除: 元のカスタム科目で、現在の行に無いもの
        for (const c of orig) {
          if (!rowIds.has(c.category_id)) {
            const r = await dispatch(deleteBudgetCategory(c.category_id));
            if (!deleteBudgetCategory.fulfilled.match(r)) {
              errors.push(formatDeleteError(c.name, r.payload));
            }
          }
        }

        // 支出は明細科目(子)を親カテゴリ(level="category")配下に作る。
        // バケットの expense_group に一致する親カテゴリを解決 (無ければ新規作成)。
        let parentCategoryId: string | undefined;
        if (isExpenseBucket && expenseGroup) {
          const existingParent = cats.find(
            (c: any) =>
              !c.deleted_at &&
              c.level === "category" &&
              (c.expense_group ?? "") === expenseGroup,
          );
          if (existingParent) {
            parentCategoryId = existingParent.category_id;
          } else {
            const pr = await dispatch(
              createBudgetCategory({
                name: expenseGroup,
                category_type: b.type,
                level: "category",
                expense_group: expenseGroup,
              } as any),
            );
            if (createBudgetCategory.fulfilled.match(pr)) {
              parentCategoryId = (pr.payload as any).category_id;
            } else {
              errors.push(
                `${expenseGroup}: ${pr.payload?.message ?? "カテゴリ作成に失敗"}`,
              );
            }
          }
        }

        // 追加・改名・並べ替え
        for (let i = 0; i < rows.length; i++) {
          const row = rows[i];
          const name = (getValues(row.id) ?? "").trim();
          if (row.isNew) {
            if (!name) continue; // 空の追加行はスキップ
            const r = await dispatch(
              createBudgetCategory({
                name,
                category_type: b.type,
                ...(isExpenseBucket && parentCategoryId
                  ? {
                      level: "item" as const,
                      parent_category_id: parentCategoryId,
                    }
                  : b.revenueGroup
                    ? { revenue_group: b.revenueGroup as BudgetRevenueGroup }
                    : expenseGroup
                      ? { expense_group: expenseGroup }
                      : {}),
              }),
            );
            if (!createBudgetCategory.fulfilled.match(r)) {
              errors.push(`${name}: ${r.payload?.message ?? "追加に失敗"}`);
            }
          } else {
            const origName = initialNamesRef.current[row.id] ?? "";
            const nameChanged = !!name && name !== origName;
            const groupChanged =
              isExpenseBucket &&
              !!expenseGroup &&
              expenseGroup !==
                (initialExpenseCategoryNamesRef.current[b.id] ?? "");
            if (nameChanged || groupChanged) {
              const r = await dispatch(
                updateBudgetCategory({
                  category_id: row.id,
                  ...(nameChanged ? { name } : {}),
                  ...(groupChanged ? { expense_group: expenseGroup } : {}),
                }),
              );
              if (!updateBudgetCategory.fulfilled.match(r)) {
                errors.push(
                  `${name || origName}: ${r.payload?.message ?? "更新に失敗"}`,
                );
              }
            }
          }
        }
      }
      await dispatch(fetchBudgetCategories());
    } catch {
      errors.push("明細項目の保存で予期しないエラー");
    }
    return errors;
  };

  useImperativeHandle(
    ref,
    () => ({
      isDirty: () => dirty,
      save: commit,
      pendingDeletions: () => {
        return collectPendingDeleteCategories().map((item) => item.name);
      },
      validateDeletions: validatePendingDeleteCategories,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dirty, buckets, bucketRows, customByBucket, deletedExpenseBucketIds],
  );

  const renderBucketRows = (b: Bucket) => {
    const systemCats = b.hideStandard ? [] : (systemByBucket[b.id] ?? []);
    const rows = bucketRows[b.id] ?? [];
    const systemOpen = expandedSystem[b.id] ?? false;
    return (
      <>
        {systemCats.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Box
              onClick={() =>
                setExpandedSystem((p) => ({ ...p, [b.id]: !systemOpen }))
              }
              sx={[
                {
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  cursor: "pointer",
                  width: "fit-content",
                  borderRadius: "8px",
                  px: 0.5,
                  py: 0.25,
                  transition: "color 0.2s",
                },
                systemOpen
                  ? {
                      color: colors.primary.main,
                      "& svg": { color: colors.primary.main },
                    }
                  : {
                      color: colors.text.primary,
                      "&:hover": {
                        color: colors.primary.main,
                        "& svg": { color: colors.primary.main },
                      },
                    },
              ]}
            >
              {systemOpen ? (
                <ExpandMoreIcon sx={{ fontSize: 14, color: "inherit" }} />
              ) : (
                <ChevronRightIcon sx={{ fontSize: 14, color: "inherit" }} />
              )}
              <Typography
                sx={{ fontSize: 12, fontWeight: 500, color: "inherit" }}
              >
                標準科目 ({systemCats.length}件)
              </Typography>
            </Box>
            <Collapse in={systemOpen}>
              <Stack spacing={0.5}>
                {systemCats.map((c) => (
                  <Box
                    key={c.category_id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1,
                      py: 0.75,
                      fontSize: 12,
                      backgroundColor: colors.background.lighter,
                      borderRadius: 1,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>{c.name}</Box>
                  </Box>
                ))}
              </Stack>
            </Collapse>
          </Box>
        )}

        {rows.length > 0 && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            onDragEnd={(e) => handleDragEnd(b.id, e)}
          >
            <SortableContext
              items={rows.map((r) => r.id)}
              strategy={verticalListSortingStrategy}
            >
              <Stack spacing={1} useFlexGap sx={{ mb: 1 }}>
                {rows.map((r) => (
                  <SortableTextRow
                    key={r.id}
                    id={r.id}
                    register={register}
                    placeholder="明細科目名"
                    onDelete={() => handleDelete(b.id, r.id)}
                    canDelete={can("master:delete")}
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>
        )}
        <Can action="master:edit">
          <CustomButton
            label="新しい明細科目を追加"
            buttonCategory="clearPrimary"
            onClick={() => handleAdd(b.id)}
            disabled={!!disabled}
            sx={{ width: "100%", borderStyle: "dashed" }}
          />
        </Can>
      </>
    );
  };

  const renderExpenseCategoryInput = (row: EditableRow) => (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <CustomTextInput
          label=""
          name={expenseCategoryFieldId(row.id)}
          register={register}
          errors={{}}
          placeholder="カテゴリ名"
          hasSpacing={false}
          inputWidth="100%"
          inputWidthSp="100%"
        />
      </Box>
      <Can action="master:delete">
        <IconButton
          size="small"
          onClick={() => handleDeleteExpenseCategory(row.id)}
          title="カテゴリを削除"
          sx={{
            color: colors.text.secondary,
            flexShrink: 0,
            "&:hover": { color: colors.status.error },
          }}
        >
          <ButtonDeleteIcon width={14} height={16} />
        </IconButton>
      </Can>
    </Stack>
  );

  return (
    <Box>
      <CustomMiddleSelect
        options={[
          { value: "income", label: "収入" },
          { value: "expense", label: "支出" },
        ]}
        value={activePane}
        onChange={(value) => setActivePane(value as BudgetCategoryPane)}
        width="100%"
        height={40}
        size="sm"
        marginBottom={2}
      />

      {activePane === "income" ? (
        <Stack spacing={2.5}>
          {KNOWN_BUCKETS.map((b) => (
            <Box key={b.id}>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: colors.text.primary,
                  mb: 1,
                }}
              >
                {b.label}
              </Typography>
              {renderBucketRows(b)}
            </Box>
          ))}
        </Stack>
      ) : (
        <Box>
          <Stack spacing={1.5}>
            {expenseCategoryRows.map((row) => {
              const bucket = expenseBuckets.find((b) => b.id === row.id);
              if (!bucket) return null;
              return (
                <Box
                  key={row.id}
                  sx={{
                    border: `1px solid ${colors.border.light}`,
                    borderRadius: "8px",
                    overflow: "hidden",
                    backgroundColor: colors.background.paper,
                  }}
                >
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1.25,
                      backgroundColor: colors.background.lighter,
                      borderBottom: `1px solid ${colors.border.light}`,
                    }}
                  >
                    {renderExpenseCategoryInput(row)}
                  </Box>
                  <Box sx={{ px: 1.5, py: 1.25 }}>
                    <Typography
                      sx={{
                        fontSize: "12px !important",
                        fontWeight: 700,
                        color: colors.text.primary,
                        mb: 0.5,
                      }}
                    >
                      明細項目
                    </Typography>
                    {renderBucketRows(bucket)}
                  </Box>
                </Box>
              );
            })}
            <Can action="master:edit">
              <CustomButton
                label="新しい支出カテゴリを追加"
                buttonCategory="clearPrimary"
                onClick={handleAddExpenseCategory}
                disabled={!!disabled}
                sx={{ width: "100%", borderStyle: "dashed" }}
              />
            </Can>
          </Stack>
        </Box>
      )}
    </Box>
  );
});

export default BudgetCategorySection;
